import React, { useState } from "react";
import {
  Box,
  Heading,
  Text,
  Image,
  Badge,
  HStack,
  VStack,
  Flex,
  Tag,
  Tooltip,
  useColorModeValue,
  useBreakpointValue 
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.css";
import { FaRocket } from "react-icons/fa";
import { getIconComponent } from "./iconUtils";

const calc = (x, y, rect) => [
  -(y - rect.top - rect.height / 2) / 30,
  (x - rect.left - rect.width / 2) / 30,
  1.05,
];

const trans = (x, y, s) =>
  `perspective(700px) rotateX(${x}deg) rotateY(${y}deg) scale(${s})`;

const MotionBox = motion(Box);

export default function FeaturedProjectCard({ project, onClick }) {
  const [style, setStyle] = useState({ transform: "none" });

  const titleSize = useBreakpointValue({ base: "md", md: "xl" });
  const badgeXPadding= useBreakpointValue({ base: 1, md: 2 });
  const badgeYPadding = useBreakpointValue({ base: 1, md: 2 });

  const tagXPadding = useBreakpointValue({ base: 2, md: 4 });

  const descSize = useBreakpointValue({ base: "xs", md: "md" });
  const tagFont = useBreakpointValue({ base: "xs", md: "sm" });
  const badgeFont = useBreakpointValue({ base: "xs", md: "sm" });
  const paddingX = useBreakpointValue({ base: 3, md: 7 });
  const paddingY = useBreakpointValue({ base: 2, md: 6 });

  // Sophisticated colors
  const glassBg = useColorModeValue(
    "rgba(23,28,38,0.88)",
    "rgba(15,17,27,0.94)"
  );
  const borderGlow = useColorModeValue(
    "0 0 24px 2px #33415522, 0 0 8px 2px #FFD58022",
    "0 0 32px 2px #2A375922, 0 0 12px 2px #FFD58022"
  );
  const cardShadow = useColorModeValue(
    "0px 8px 28px 0px #23293b55",
    "0px 10px 40px 0px #161b2633"
  );
  const badgeBg = "#23293b";
  const featuredBg = "#FFD580";
  const featuredText = "#23293b";
  const techPillBg = "#293040";
  const apiPillBg = "#353b4a";
  const categoryBg = "#23293b";
  const categoryText = "#F7F7F8";
  const titleColor = "#F7F7F8";
  const tagShadow = "0 2px 6px #23293b22";

  // 3D tilt handlers
  function handleMouseMove(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const [x, y, s] = calc(e.clientX, e.clientY, rect);
    setStyle({ transform: trans(x, y, s) });
  }
  function handleMouseLeave() {
    setStyle({ transform: "none" });
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: 40, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 120, damping: 12 }}
      whileHover={{
        boxShadow: borderGlow,
        scale: 1.03,
      }}
      style={style}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      bg={glassBg}
      backdropFilter="blur(16px) saturate(180%)"
      borderRadius="2xl"
      boxShadow={cardShadow}
      p={0}
      overflow="visible"
      position="relative"
      cursor="pointer"
      minH={{ base: "380px", md: "440px" }}
      onClick={onClick}
    >
      {/* Subtle Border */}
      <Box
        pos="absolute"
        inset="0"
        zIndex="1"
        pointerEvents="none"
        borderRadius="2xl"
        boxShadow={borderGlow}
        transition="box-shadow 0.4s"
      />

      {/* Subtle Overlay */}
      <Box
        position="absolute"
        top="0"
        left="0"
        w="full"
        h="full"
        pointerEvents="none"
        zIndex="2"
      >
        <svg width="100%" height="100%">
          <circle
            cx="30%"
            cy="15%"
            r="24"
            fill="#33415511"
            style={{ filter: "blur(8px)" }}
          />
          <circle
            cx="78%"
            cy="70%"
            r="18"
            fill="#FFD58015"
            style={{ filter: "blur(8px)" }}
          />
          <rect
            x="65%"
            y="10%"
            width="32"
            height="32"
            fill="#3b3b3b11"
            rx="12"
            style={{ filter: "blur(6px)" }}
          />
        </svg>
      </Box>

      {/* Featured Badge */}
      <Badge
        pos="absolute"
        left={5}
        top={5}
        zIndex="5"
        px={badgeXPadding}
        py={badgeYPadding}
        fontSize={badgeFont}
        borderRadius="full"
        bg={featuredBg}
        color={featuredText}
        fontWeight="bold"
        boxShadow={tagShadow}
        leftIcon={<FaRocket />}
        // No glow, no animated boxShadow, clean
      >
        Featured
      </Badge>
      {project.category && (
        <Tag
          pos="absolute"
          right={5}
          top={5}
          zIndex={5}
          px={badgeXPadding}
          py={badgeYPadding}
          fontWeight="bold"
          fontSize={tagFont}
          borderRadius="full"
          bg={categoryBg}
          color={categoryText}
          boxShadow={tagShadow}
        >
          {project.category}
        </Tag>
      )}

      {/* Swiper Gallery */}
      <Box pos="relative" zIndex="3" borderTopRadius="2xl" overflow="hidden" h="260px">
        <Swiper
          style={{ borderTopLeftRadius: "1.5rem", borderTopRightRadius: "1.5rem", width: "100%", height: "100%" }}
          slidesPerView={1}
          pagination={{ clickable: true }}
          loop
        >
          {project.modalImages.map((img, i) => (
            <SwiperSlide key={i}>
              <Image
                src={img}
                alt={project.title + " image " + (i + 1)}
                w="100%"
                h="100%"
                objectFit="cover"
                borderTopRadius="2xl"
                transition="transform .5s"
                _hover={{ transform: "scale(1.03)" }}
                draggable={false}
                userSelect="none"
                loading="lazy"
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      {/* Content */}
      <VStack
        align="start"
        px={paddingX}
        py={paddingY}
        zIndex="10"
        position="relative"
        spacing={3}
      >
        <Heading
          as="h3"
          size={titleSize}
          fontWeight="extrabold"
          color={titleColor}
          letterSpacing="wide"
          mb={1}
          lineHeight={1.15}
          // No textShadow!
        >
          {project.title}
        </Heading>
        <Text fontSize={descSize} color={useColorModeValue("gray.200", "#b8d1f1")}>
          {project.description}
        </Text>
        {/* Tech Stack Pills */}
        {project.techStack && (
          <HStack mt={2} flexWrap="wrap" gap={2}>
            {project.techStack.map((tech, idx) => (
              <Tag
                key={idx}
                display="flex"
                alignItems="center"
                px={badgeXPadding}
                py={badgeYPadding}
                borderRadius="full"
                bg={techPillBg}
                color="#F7F7F8"
                fontWeight="bold"
                boxShadow={tagShadow}
                fontSize={tagFont}
                gap={2}
                _hover={{ filter: "brightness(1.08)", boxShadow: "md" }}
              >
                {getIconComponent(tech.icon)}
                <span>{tech.title}</span>
              </Tag>
            ))}
          </HStack>
        )}
        {/* External APIs */}
        {project.externalAPIs && project.externalAPIs.length > 0 && (
          <Box pt={2} w="full">
            <Text fontWeight="bold" fontSize={descSize} color="#FFD580" pb={1}>
              External APIs
            </Text>
            <HStack flexWrap="wrap" gap={3} w="full">
              {project.externalAPIs.map((api, idx) => (
                <Tooltip label={api.description} fontSize="sm" key={idx}>
                  <Tag
                    px={tagXPadding}
                    py={badgeYPadding}
                    borderRadius="full"
                    bg={apiPillBg}
                    color="#F7F7F8"
                    fontWeight="bold"
                    fontSize={tagFont}
                    boxShadow={tagShadow}
                    gap={2}
                    _hover={{
                      filter: "brightness(1.10)",
                      boxShadow: "0 0 10px #23293b44",
                      transform: "scale(1.08)",
                    }}
                    transition="all .2s cubic-bezier(.6,.3,.25,1)"
                  >
                    {getIconComponent(api.icon)}
                    <span>{api.title}</span>
                  </Tag>
                </Tooltip>
              ))}
            </HStack>
          </Box>
        )}
      </VStack>
    </MotionBox>
  );
}
